import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import { orgBillingApi, type OrgSubscriptionOverflowError } from "@/api/org-billing-api";
import { usePlans } from "@/hooks/use-plans";
import { DowngradeDealSelectionModal } from "@/components/modals/downgrade-deal-selection-modal";
import type { Plan, PlanCadence } from "@/api/plans-api";

export type CadenceId = "monthly" | "quarterly" | "semi-annual" | "annual";

export const PLAN_KEYS = ["plus", "pro", "elite"] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

// Stripe price nicknames are free-form text. Narrow safely on the first
// whitespace-delimited token; unrecognized strings return undefined (caller
// treats as no-current-plan). Remove once BE returns a canonical plan_tier
// on the org subscription.
export function toPlanKey(input?: string): PlanKey | undefined {
  const firstWord = input?.toLowerCase().trim().split(/\s+/)[0];
  if (!firstWord) return undefined;
  const direct = PLAN_KEYS.find((k) => k === firstWord);
  if (direct) return direct;
  if (firstWord === "premium") return "pro";
  if (firstWord === "enterprise") return "elite";
  return undefined;
}

const CADENCE_LABELS: Record<CadenceId, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  "semi-annual": "Semi-Annual",
  annual: "Annual",
};

const monthsInCycle = (cadence: PlanCadence): number => {
  if (cadence.interval === "year") return 12 * (cadence.interval_count ?? 1);
  return cadence.interval_count ?? 1;
};

const monthlyDisplay = (cadence: PlanCadence): number =>
  Math.round(cadence.amount_cents / monthsInCycle(cadence) / 100);

// Savings vs the monthly cadence of the same plan. Rounded to nearest %.
const savingsPct = (cadence: PlanCadence, monthly?: PlanCadence): number | null => {
  if (!monthly || cadence.id === "monthly") return null;
  const monthlyPerMonth = monthly.amount_cents / monthsInCycle(monthly);
  const cadencePerMonth = cadence.amount_cents / monthsInCycle(cadence);
  if (monthlyPerMonth <= 0) return null;
  const pct = 1 - cadencePerMonth / monthlyPerMonth;
  if (pct <= 0) return null;
  return Math.round(pct * 100);
};

type OverflowError = {
  planId: PlanKey | "free";
  code: "premium_deals_exceed_cap" | "workspaces_exceed_cap";
  current: number;
  newCap: number;
  remediation: string;
};

export default function PlanGrid({
  currentPlanKey,
  orgId,
  initialCadence = "annual",
}: {
  currentPlanKey?: PlanKey;
  orgId?: string;
  initialCadence?: CadenceId;
}) {
  const queryClient = useQueryClient();
  const { plans, isLoading } = usePlans();
  const [cadence, setCadence] = useState<CadenceId>(initialCadence);
  const [overflow, setOverflow] = useState<OverflowError | null>(null);
  const [isPending, setIsPending] = useState<PlanKey | "free" | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<PlanKey | null>(null);
  const [selection, setSelection] = useState<{
    planId: PlanKey | "free";
    cadence?: CadenceId;
    newCap: number;
  } | null>(null);
  const [workspaceConfirm, setWorkspaceConfirm] = useState<{
    planId: PlanKey | "free";
    cadence?: CadenceId;
    current: number;
    newCap: number;
  } | null>(null);

  const cadenceIds: CadenceId[] = [
    "monthly",
    "quarterly",
    "semi-annual",
    "annual",
  ];

  // Drive the toggle's savings label off whichever plan is most prominent
  // (popular plan, or the first plan). Discount % is typically uniform across
  // plans; if Stripe diverges we still show one consistent number.
  const referencePlan = plans.find((p) => p.popular) ?? plans[0];
  const referenceMonthly = referencePlan?.cadences.find((c) => c.id === "monthly");

  const cadenceSavings = (id: CadenceId): number | null => {
    const c = referencePlan?.cadences.find((x) => x.id === id);
    if (!c) return null;
    return savingsPct(c, referenceMonthly);
  };

  const buttonLabelFor = (planTier: PlanKey): {
    label: string;
    variant: "default" | "outline" | "secondary";
    disabled: boolean;
  } => {
    if (currentPlanKey === planTier) {
      return { label: "Current plan", variant: "secondary", disabled: true };
    }
    if (!currentPlanKey) {
      return { label: "Upgrade", variant: "default", disabled: false };
    }
    const targetIdx = PLAN_KEYS.indexOf(planTier);
    const currentIdx = PLAN_KEYS.indexOf(currentPlanKey);
    if (targetIdx > currentIdx) {
      return { label: "Upgrade", variant: "default", disabled: false };
    }
    return { label: "Switch", variant: "outline", disabled: false };
  };

  const formatEffective = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString() : "the end of the current period";

  // Premium-deal overflow opens the selection modal; workspace overflow keeps
  // the inline card (its remediation is "archive workspaces", not a choice).
  const routeOverflow = (
    planId: PlanKey | "free",
    changeCadence: CadenceId | undefined,
    data: OrgSubscriptionOverflowError
  ) => {
    if (data.code === "premium_deals_exceed_cap") {
      setSelection({ planId, cadence: changeCadence, newCap: data.new_cap });
      return;
    }
    setWorkspaceConfirm({
      planId,
      cadence: changeCadence,
      current: data.current,
      newCap: data.new_cap,
    });
  };

  const handleChange = async (planTier: PlanKey) => {
    setOverflow(null);
    if (!orgId) return;

    if (!currentPlanKey) {
      setIsPending(planTier);
      try {
        const returnUrl = `${window.location.origin}${window.location.pathname}`;
        // Success carries a marker so the billing page can tell it apart from
        // a cancel (both land on the same path) and force-refresh the cached
        // subscription/quota queries.
        const session = await orgBillingApi.startSubscriptionCheckout(
          orgId,
          planTier,
          cadence,
          `${returnUrl}?subscription=success`,
          returnUrl
        );
        window.location.href = session.checkout_url;
      } catch (err) {
        console.error("Checkout start failed", err);
        toast.error(getErrorMessage(err, "Couldn't start checkout."));
        setIsPending(null);
      }
      return;
    }

    setConfirmTarget(planTier);
  };

  const executeChange = async (planTier: PlanKey) => {
    if (!orgId) return;
    setConfirmTarget(null);
    setIsPending(planTier);
    try {
      const result = await orgBillingApi.changeSubscription(
        orgId,
        planTier,
        cadence
      );
      toast.success(
        result.status === "already_on_plan"
          ? "You're already on this plan."
          : result.status === "active"
          ? `Upgraded to ${planTier}.`
          : `${planTier} change scheduled for ${formatEffective(result.effective_at)}.`
      );
      queryClient.invalidateQueries({ queryKey: ["orgSubscription"] });
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        routeOverflow(
          planTier,
          cadence,
          err.response.data as OrgSubscriptionOverflowError
        );
        return;
      }
      console.error("Plan change failed", err);
      toast.error(getErrorMessage(err, "Couldn't change plan."));
    } finally {
      setIsPending(null);
    }
  };

  const handleDowngradeToFree = async () => {
    setOverflow(null);
    if (!orgId) return;

    setIsPending("free");
    try {
      const result = await orgBillingApi.changeSubscription(orgId, "free");
      toast.success(
        result.status === "already_free"
          ? "Already on Free tier."
          : `Downgrade to Free scheduled for ${formatEffective(result.effective_at)}.`
      );
      queryClient.invalidateQueries({ queryKey: ["orgSubscription"] });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        routeOverflow(
          "free",
          undefined,
          err.response.data as OrgSubscriptionOverflowError
        );
        return;
      }
      console.error("Downgrade-to-Free failed", err);
      toast.error(getErrorMessage(err, "Couldn't downgrade to Free."));
    } finally {
      setIsPending(null);
    }
  };

  const handleSelectionConfirm = async (keepApplicationIds: string[]) => {
    if (!orgId || !selection) return;
    const { planId, cadence: selectedCadence } = selection;
    setIsPending(planId);
    try {
      const result = await orgBillingApi.changeSubscription(
        orgId,
        planId,
        planId === "free" ? undefined : selectedCadence,
        keepApplicationIds,
        true
      );
      toast.success(
        result.status === "already_free"
          ? "Already on Free tier."
          : `${
              planId === "free" ? "Downgrade to Free" : `${planId} change`
            } scheduled for ${formatEffective(result.effective_at)}.`
      );
      queryClient.invalidateQueries({ queryKey: ["orgSubscription"] });
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      queryClient.invalidateQueries({ queryKey: ["orgPremiumDeals"] });
      setSelection(null);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        const data = err.response.data as OrgSubscriptionOverflowError;
        // A different cap (e.g. workspaces) can still block the downgrade even
        // when the deal selection is valid — close the modal and surface that
        // remediation inline rather than implying the selection was wrong.
        if (data.code !== "premium_deals_exceed_cap") {
          setSelection(null);
          setOverflow({
            planId,
            code: data.code,
            current: data.current,
            newCap: data.new_cap,
            remediation: data.remediation,
          });
          return;
        }
        toast.error(
          getErrorMessage(
            err,
            "That selection still exceeds the new plan's limit."
          )
        );
        return;
      }
      console.error("Downgrade with selection failed", err);
      toast.error(getErrorMessage(err, "Couldn't change plan."));
    } finally {
      setIsPending(null);
    }
  };

  const handleWorkspaceConfirm = async () => {
    if (!orgId || !workspaceConfirm) return;
    const { planId, cadence: selectedCadence } = workspaceConfirm;
    setWorkspaceConfirm(null);
    setIsPending(planId);
    try {
      const result = await orgBillingApi.changeSubscription(
        orgId,
        planId,
        planId === "free" ? undefined : selectedCadence,
        undefined,
        true
      );
      toast.success(
        result.status === "already_free"
          ? "Already on Free tier."
          : `${
              planId === "free" ? "Downgrade to Free" : `${planId} change`
            } scheduled for ${formatEffective(result.effective_at)}.`
      );
      queryClient.invalidateQueries({ queryKey: ["orgSubscription"] });
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        const data = err.response.data as OrgSubscriptionOverflowError;
        // Workspace is acknowledged now; a remaining premium overflow opens the
        // keep-deals modal (whose confirm re-sends the acknowledge flag).
        if (data.code === "premium_deals_exceed_cap") {
          setSelection({ planId, cadence: selectedCadence, newCap: data.new_cap });
          return;
        }
        setOverflow({
          planId,
          code: data.code,
          current: data.current,
          newCap: data.new_cap,
          remediation: data.remediation,
        });
        return;
      }
      console.error("Workspace-overflow downgrade failed", err);
      toast.error(getErrorMessage(err, "Couldn't change plan."));
    } finally {
      setIsPending(null);
    }
  };

  if (isLoading && plans.length === 0) {
    return (
      <section id="plan-grid" className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </section>
    );
  }

  return (
    <section id="plan-grid">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
          {currentPlanKey ? "Change plan" : "Choose a plan"}
        </h2>
      </div>

      <div className="mt-3 flex justify-center">
        <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
          {cadenceIds.map((id) => {
            const savings = cadenceSavings(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => setCadence(id)}
                className={cn(
                  "flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors",
                  cadence === id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {CADENCE_LABELS[id]}
                {savings !== null && (
                  <span className="text-[10px] text-emerald-600">-{savings}%</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => {
          if (plan.tier === "free") {
            const isCurrentFree = !currentPlanKey;
            const overflowOnThisCard =
              overflow?.planId === "free" ? overflow : null;
            return (
              <div
                key="free"
                className={cn(
                  "relative flex flex-col rounded-lg border bg-card p-5",
                  isCurrentFree ? "border-foreground" : "border-border"
                )}
              >
                {isCurrentFree && (
                  <div className="absolute -top-2.5 left-5 rounded-full border border-foreground bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
                    Current
                  </div>
                )}
                <h3 className="text-base font-semibold text-foreground">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-semibold text-foreground">$0</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
                <p className="text-xs text-muted-foreground">no billing</p>

                <ul className="mt-4 space-y-2 text-sm">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-muted-foreground">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {overflowOnThisCard && (
                  <div className="mt-4 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                    <p>
                      You have {overflowOnThisCard.current}{" "}
                      {overflowOnThisCard.code === "premium_deals_exceed_cap"
                        ? "premium deals"
                        : "workspaces"}
                      . Free only allows {overflowOnThisCard.newCap}.
                    </p>
                    <p className="mt-1">{overflowOnThisCard.remediation}</p>
                  </div>
                )}

                <div className="mt-auto pt-5">
                  <Button
                    className="w-full"
                    variant={isCurrentFree ? "secondary" : "outline"}
                    disabled={isCurrentFree || isPending === "free" || !orgId}
                    onClick={handleDowngradeToFree}
                  >
                    {isCurrentFree
                      ? "Current plan"
                      : isPending === "free"
                      ? "Updating…"
                      : "Switch to Free"}
                  </Button>
                </div>
              </div>
            );
          }

          const planTier = plan.tier as PlanKey;
          const selectedCadence = plan.cadences.find((c) => c.id === cadence);
          if (!selectedCadence) return null;

          const displayMonthly = monthlyDisplay(selectedCadence);
          const cadenceLabel =
            cadence === "monthly"
              ? "billed monthly"
              : `billed ${CADENCE_LABELS[cadence].toLowerCase()}`;
          const isCurrent = currentPlanKey === planTier;
          const btn = buttonLabelFor(planTier);
          const overflowOnThisCard =
            overflow?.planId === planTier ? overflow : null;

          return (
            <div
              key={plan.tier}
              className={cn(
                "relative flex flex-col rounded-lg border bg-card p-5",
                isCurrent
                  ? "border-foreground"
                  : plan.popular
                  ? "border-foreground"
                  : "border-border"
              )}
            >
              {isCurrent ? (
                <div className="absolute -top-2.5 left-5 rounded-full border border-foreground bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
                  Current
                </div>
              ) : plan.popular ? (
                <div className="absolute -top-2.5 left-5 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background">
                  Most popular
                </div>
              ) : null}
              <h3 className="text-base font-semibold text-foreground">{plan.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-semibold text-foreground">
                  ${displayMonthly}
                </span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>
              <p className="text-xs text-muted-foreground">{cadenceLabel}</p>

              <ul className="mt-4 space-y-2 text-sm">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-muted-foreground">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {overflowOnThisCard && (
                <div className="mt-4 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  <p>
                    You have {overflowOnThisCard.current}{" "}
                    {overflowOnThisCard.code === "premium_deals_exceed_cap"
                      ? "premium deals"
                      : "workspaces"}
                    . {plan.name} only allows {overflowOnThisCard.newCap}.
                  </p>
                  <p className="mt-1">{overflowOnThisCard.remediation}</p>
                </div>
              )}

              <div className="mt-auto pt-5">
                <Button
                  className="w-full"
                  variant={btn.variant}
                  disabled={btn.disabled || isPending === planTier}
                  onClick={() => handleChange(planTier)}
                >
                  {isPending === planTier ? "Updating…" : btn.label}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <ChangePlanConfirmDialog
        target={confirmTarget}
        currentPlanKey={currentPlanKey}
        cadence={cadence}
        plans={plans}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={(planTier) => executeChange(planTier)}
      />

      <DowngradeDealSelectionModal
        open={!!selection}
        onOpenChange={(open) => {
          if (!open) setSelection(null);
        }}
        orgId={orgId}
        targetPlanName={
          selection
            ? selection.planId === "free"
              ? "Free"
              : plans.find((p) => p.tier === selection.planId)?.name ??
                selection.planId
            : ""
        }
        newCap={selection?.newCap ?? 0}
        isSubmitting={isPending !== null}
        onConfirm={handleSelectionConfirm}
      />

      <AlertDialog
        open={!!workspaceConfirm}
        onOpenChange={(open) => {
          if (!open) setWorkspaceConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Over the workspace limit</AlertDialogTitle>
            <AlertDialogDescription>
              {workspaceConfirm
                ? `After downgrading you'll have ${workspaceConfirm.current} workspaces, over the ${
                    workspaceConfirm.planId === "free"
                      ? "Free"
                      : plans.find((p) => p.tier === workspaceConfirm.planId)
                          ?.name ?? workspaceConfirm.planId
                  } limit of ${workspaceConfirm.newCap}. You won't be able to create new workspaces until you're back under the limit. Continue?`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWorkspaceConfirm}>
              Continue downgrade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function ChangePlanConfirmDialog({
  target,
  currentPlanKey,
  cadence,
  plans,
  onCancel,
  onConfirm,
}: {
  target: PlanKey | null;
  currentPlanKey?: PlanKey;
  cadence: CadenceId;
  plans: Plan[];
  onCancel: () => void;
  onConfirm: (planTier: PlanKey) => void;
}) {
  const targetPlan = target ? plans.find((p) => p.tier === target) : undefined;
  const targetCadence = targetPlan?.cadences.find((c) => c.id === cadence);
  if (!target || !targetPlan || !targetCadence || !currentPlanKey) return null;

  const isUpgrade =
    PLAN_KEYS.indexOf(target) > PLAN_KEYS.indexOf(currentPlanKey);
  const monthly = monthlyDisplay(targetCadence);
  const cadenceLabel = CADENCE_LABELS[cadence].toLowerCase();

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isUpgrade ? "Upgrade" : "Switch"} to {targetPlan.name}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                New price:{" "}
                <span className="font-medium text-foreground">
                  ${monthly}/mo
                </span>{" "}
                billed {cadenceLabel}.
              </p>
              <p>
                {isUpgrade
                  ? "Your card on file will be charged a prorated amount today, and the new plan takes effect immediately."
                  : "Your current plan stays active until the end of the billing period. The change is then applied at no extra charge."}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(target)}>
            {isUpgrade ? "Upgrade now" : "Confirm switch"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
